import React, { useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import routes from 'routes';
import * as Icon from 'react-feather';
import { list_my_products } from 'services/apiSrv';
import { useQuery } from 'react-query'; // or @tanstack/react-query if using v5
import Loader from 'components/widgets/loader';
import ProductCard from 'components/productcard/ProductCard';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { list_intermediary_underwriters } from 'services/apiSrv';

function Products() {
  const history = useHistory();
  const [currentPage] = React.useState(1); // keeping it but not used yet
  const itemsPerPage = 5;
  console.log("Products component rendered", Date.now());

  // ────────────────────────────────────────────────
  //  Fixed infinite refetch + better practices
  // ────────────────────────────────────────────────
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['my-products'],           // more descriptive key
    queryFn: list_my_products,
    staleTime: 3 * 60 * 1000,            // 3 minutes – prevents refetch on every render
    gcTime: 10 * 60 * 1000,              // keep in cache longer (was cacheTime in RQ v4)
    refetchOnWindowFocus: false,         // usually good default for dashboard views
    retry: 1,                            // don't retry forever on failure
  });

  // Extract products once – prevents unnecessary re-renders
  const products = useMemo(() => {
    return data?.data?._value ?? [];
  }, [data]);

  // Debug only – won't trigger re-renders
  useEffect(() => {
    console.log("Products loaded:", products.length, products);
    console.log("Error:", error);
  }, [products, error]);

  const handleView = useCallback((product) => {
    console.log("Viewing product:", product);
    history.push(`/dashboard/setup/product/${product.productSystemRef}`);
  }, [history]);

  const handleEdit = useCallback((product) => {
    const ref = product.productSystemRef;
    const cleaned = ref?.split('/').pop() || ref || '';
    console.log("Editing product:", cleaned);
    history.push(`/dashboard/setup/product/${ref}/edit`);
  }, [history]);

  const handleDelete = useCallback((id) => {
    console.log(`Delete requested for product: ${id}`);
    // TODO: implement actual delete + invalidate query
    // queryClient.invalidateQueries(['my-products'])
  }, []);

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="alert alert-danger">
        Failed to load products: {error.message || 'Unknown error'}
      </div>
    );
  }

  // ────────────────────────────────────────────────
  //  Styled Components
  // ────────────────────────────────────────────────
  const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
    padding: 16px 0;
  `;

  const HeaderSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    @media (min-width: 576px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  `;

  const ContentCard = styled.div`
    border-radius: 12px;
    background: #ffffff;
    padding: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  `;

  // ────────────────────────────────────────────────
  //  Render
  // ────────────────────────────────────────────────
  return (
    <div className="container-fluid px-0" style={{ width: '100%', maxWidth: '100%' }}>
      {/* You can keep or remove the stats cards – keeping minimal version */}
      {/* <StatsCardsRow /> */} 

      <ContentCard>
        <HeaderSection>
          <h1 className="h3 mb-0 text-gray-800">My Products</h1>
          <button
            className="btn btn-success px-4 py-2"
            onClick={() => history.push(routes.addproduct)}
          >
            <Icon.Plus size={16} className="mr-2" />
            Add Product
          </button>
        </HeaderSection>

        {isFetching && products.length > 0 && (
          <div className="text-muted small mb-3">Refreshing...</div>
        )}

        {products.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <Icon.Package size={48} className="mb-3 opacity-50" />
            <p>No products found yet.</p>
            <button
              className="btn btn-outline-success mt-2"
              onClick={() => history.push(routes.addproduct)}
            >
              Create your first product
            </button>
          </div>
        ) : (
          <CardGrid>
            {products.map((product, index) => (
              <ProductCard
                key={product.productSystemRef || product.id || index}
                product={product}
                onView={() => handleView(product)}
                onEdit={() => handleEdit(product)}
                onDelete={() => handleDelete(product.productSystemRef)}
              />
            ))}
          </CardGrid>
        )}
      </ContentCard>
    </div>
  );
}

export default Products;